import { useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { createFileRoute, useRouter, useRouterState } from '@tanstack/react-router';
import {
  CheckCircle2,
  GitBranch,
  MoreHorizontal,
  Pencil,
  Plus,
  SlidersHorizontal,
  Star,
  Trash2,
  X,
} from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/atoms/sheet';
import { DetailPageLayout } from '@/components/layouts';
import {
  DataTableDrawerActionFooter,
  DataTableDrawerHeader,
  DataTablePaginationFooter,
  DataTableSearchFilterField,
  DataTableSelectFilterField,
  SearchInput,
} from '@/components/molecules';
import {
  DataTableFilterSection,
  FinancialDataTable,
  type FinancialDataTableColumn,
  type FinancialDataTableSortDir,
} from '@/components/organisms';
import {
  PipelineFormContent,
  type PipelineFormValues,
  type PipelineStageDraft,
} from '@/components/organisms/settings-job-pipelines/PipelineFormDrawer';
import {
  formatCorePhase,
  stageDraftsFromPipeline,
  stageDraftTriggerChanged,
  stageDraftTriggerPayload,
  stageDraftsToCreatePayload,
} from '@/components/organisms/settings-job-pipelines/pipelineFormUtils';
import { Fullbleed } from '@/contexts/fullbleed';
import { useCreatePipeline } from '@/hooks/pipelines/useCreatePipeline';
import { useCreateStage } from '@/hooks/pipelines/useCreateStage';
import { useDeletePipeline } from '@/hooks/pipelines/useDeletePipeline';
import { useDeleteStage } from '@/hooks/pipelines/useDeleteStage';
import { useJobPipelines } from '@/hooks/pipelines/useJobPipelines';
import { useUpdatePipeline } from '@/hooks/pipelines/useUpdatePipeline';
import { useUpdateStage } from '@/hooks/pipelines/useUpdateStage';
import { CORE_PHASE_COLORS, type CorePhase, type JobPipeline } from '@/types/pipeline';

export const Route = createFileRoute('/_dashboard/dashboard/settings/job-pipelines/')({
  component: JobPipelinesPage,
});

const PAGE_SIZE = 25;
const NEW_ROUTE_PATTERN = /^\/dashboard\/settings\/job-pipelines\/new\/?$/;
const EDIT_ROUTE_PATTERN = /^\/dashboard\/settings\/job-pipelines\/([^/]+)\/edit\/?$/;
const DETAIL_ROUTE_PATTERN = /^\/dashboard\/settings\/job-pipelines\/([^/]+)\/?$/;

type PipelineColumn = 'pipeline' | 'default' | 'stages' | 'phases' | 'terminal' | 'updatedAt';
type DefaultFilter = 'all' | 'default' | 'custom';

const PIPELINE_COLUMNS: FinancialDataTableColumn<PipelineColumn>[] = [
  { key: 'pipeline', label: 'Pipeline', sortable: true, width: 360 },
  {
    key: 'default',
    label: 'Default',
    sortable: true,
    align: 'center',
    width: 120,
  },
  {
    key: 'stages',
    label: 'Stages',
    sortable: true,
    align: 'center',
    width: 110,
  },
  { key: 'phases', label: 'Core Phases', sortable: true, width: 360 },
  {
    key: 'terminal',
    label: 'Terminal',
    sortable: true,
    align: 'center',
    width: 120,
  },
  {
    key: 'updatedAt',
    label: 'Updated',
    sortable: true,
    defaultVisible: false,
    width: 160,
  },
];

const DEFAULT_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'default', label: 'Default' },
  { value: 'custom', label: 'Custom' },
];

export function JobPipelinesPage() {
  const router = useRouter();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });

  const createOpen = NEW_ROUTE_PATTERN.test(pathname);
  const routeEditId = pathname.match(EDIT_ROUTE_PATTERN)?.[1];
  const decodedEditPipelineId = routeEditId ? decodeURIComponent(routeEditId) : undefined;
  const rawDetailId = pathname.match(DETAIL_ROUTE_PATTERN)?.[1];
  const detailPipelineId =
    rawDetailId && rawDetailId !== 'new' ? decodeURIComponent(rawDetailId) : undefined;

  const { data: pipelines = [], isLoading } = useJobPipelines();
  const createPipeline = useCreatePipeline();
  const updatePipeline = useUpdatePipeline();
  const deletePipeline = useDeletePipeline();
  const createStage = useCreateStage();
  const updateStage = useUpdateStage();
  const deleteStage = useDeleteStage();

  const [search, setSearch] = useState('');
  const [defaultFilter, setDefaultFilter] = useState<DefaultFilter>('all');
  const [sortKey, setSortKey] = useState<PipelineColumn>('pipeline');
  const [sortDir, setSortDir] = useState<FinancialDataTableSortDir>('asc');
  const [page, setPage] = useState(1);
  const [archiveTarget, setArchiveTarget] = useState<JobPipeline | null>(null);

  const editPipeline = useMemo(
    () => pipelines.find((p) => p.id === decodedEditPipelineId) ?? null,
    [decodedEditPipelineId, pipelines],
  );
  const selectedPipeline = useMemo(
    () => (detailPipelineId ? (pipelines.find((p) => p.id === detailPipelineId) ?? null) : null),
    [detailPipelineId, pipelines],
  );

  const filteredPipelines = useMemo(() => {
    const query = search.trim().toLowerCase();
    return pipelines.filter((pipeline) => {
      const stages = sortedStages(pipeline);
      const matchesSearch =
        !query ||
        [
          pipeline.name,
          ...stages.map((stage) => stage.name),
          ...stages.map((stage) => stage.corePhase),
        ]
          .join(' ')
          .toLowerCase()
          .includes(query);
      const matchesDefault =
        defaultFilter === 'all' ||
        (defaultFilter === 'default' && pipeline.isDefault) ||
        (defaultFilter === 'custom' && !pipeline.isDefault);
      return matchesSearch && matchesDefault;
    });
  }, [defaultFilter, pipelines, search]);

  const sortedPipelines = useMemo(() => {
    return [...filteredPipelines].sort((a, b) => {
      const result = compareValues(getSortValue(sortKey, a), getSortValue(sortKey, b));
      return sortDir === 'asc' ? result : -result;
    });
  }, [filteredPipelines, sortDir, sortKey]);

  const activeFilterCount = defaultFilter !== 'all' ? 1 : 0;
  const totalPages = Math.max(1, Math.ceil(sortedPipelines.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagePipelines = sortedPipelines.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );
  const isDrawerPending =
    createPipeline.isPending ||
    updatePipeline.isPending ||
    deletePipeline.isPending ||
    createStage.isPending ||
    updateStage.isPending ||
    deleteStage.isPending;

  function resetFilters() {
    setSearch('');
    setDefaultFilter('all');
    setPage(1);
  }

  function handleSort(column: FinancialDataTableColumn<PipelineColumn>) {
    const key = column.key;
    setPage(1);
    if (sortKey === key) {
      setSortDir((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir('asc');
  }

  function openCreateDrawer() {
    void router.navigate({ to: '/dashboard/settings/job-pipelines/new' });
  }

  function openEditDrawer(pipelineId: string) {
    void router.navigate({
      to: '/dashboard/settings/job-pipelines/$id/edit',
      params: { id: pipelineId },
    });
  }

  function openDetail(pipelineId: string) {
    void router.navigate({
      to: '/dashboard/settings/job-pipelines/$id',
      params: { id: pipelineId },
    });
  }

  function closeDrawer() {
    void router.navigate({ to: '/dashboard/settings/job-pipelines' });
  }

  async function syncStages(
    pipelineId: string,
    previous: JobPipeline | null,
    next: PipelineStageDraft[],
  ) {
    const initialStageList = stageDraftsFromPipeline(previous);
    const nextById = new Map(next.filter((stage) => stage.id).map((stage) => [stage.id!, stage]));

    await Promise.all([
      ...initialStageList
        .filter((stage) => stage.id && !nextById.has(stage.id))
        .map((stage) => deleteStage.mutateAsync({ pipelineId, stageId: stage.id! })),
      ...next.map((stage, index) => {
        const payload = {
          name: stage.name,
          corePhase: stage.corePhase,
          color: stage.color,
          isTerminal: stage.isTerminal,
          ...stageDraftTriggerPayload(stage),
          sortOrder: index,
        };

        if (!stage.id) {
          return createStage.mutateAsync({ pipelineId, ...payload });
        }

        const original = initialStageList.find((item) => item.id === stage.id);
        if (
          !original ||
          original.name !== stage.name ||
          original.corePhase !== stage.corePhase ||
          original.color !== stage.color ||
          original.isTerminal !== stage.isTerminal ||
          stageDraftTriggerChanged(original, stage) ||
          initialStageList.findIndex((item) => item.id === stage.id) !== index
        ) {
          return updateStage.mutateAsync({
            pipelineId,
            stageId: stage.id,
            ...payload,
          });
        }

        return Promise.resolve();
      }),
    ]);
  }

  async function handleCreate(values: PipelineFormValues, stages: PipelineStageDraft[]) {
    const createStages = stageDraftsToCreatePayload(stages);
    await createPipeline.mutateAsync({
      name: values.name,
      isDefault: values.isDefault,
      ...(createStages.length > 0 ? { stages: createStages } : {}),
    });
    closeDrawer();
  }

  async function handleUpdate(
    pipelineId: string,
    values: PipelineFormValues,
    stages: PipelineStageDraft[],
  ) {
    const previous = pipelines.find((p) => p.id === pipelineId) ?? null;
    await updatePipeline.mutateAsync({
      id: pipelineId,
      name: values.name,
      isDefault: values.isDefault,
    });
    await syncStages(pipelineId, previous, stages);
    closeDrawer();
  }

  function renderCell(key: PipelineColumn, pipeline: JobPipeline) {
    const stages = sortedStages(pipeline);
    const terminalCount = stages.filter((stage) => stage.isTerminal).length;
    const uniquePhases = uniqueCorePhases(stages);

    switch (key) {
      case 'pipeline':
        return (
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border bg-muted">
              <GitBranch className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-2">
                <p className="truncate text-sm font-medium">{pipeline.name}</p>
                {pipeline.isDefault ? (
                  <Badge variant="secondary" className="shrink-0 gap-1 text-[10px]">
                    <Star className="h-3 w-3" />
                    Default
                  </Badge>
                ) : null}
              </div>
              <p className="truncate text-xs text-muted-foreground">
                {stages.length} {stages.length === 1 ? 'stage' : 'stages'}
              </p>
            </div>
          </div>
        );
      case 'default':
        return pipeline.isDefault ? (
          <Badge variant="default" className="gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Default
          </Badge>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      case 'stages':
        return <span className="tabular-nums">{stages.length}</span>;
      case 'phases':
        return uniquePhases.length > 0 ? (
          <div className="flex min-w-0 flex-wrap gap-1.5">
            {uniquePhases.slice(0, 4).map((phase) => (
              <Badge key={phase.corePhase} variant="outline" className="gap-1 text-[10px]">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: phase.color }} />
                {formatCorePhase(phase.corePhase)}
              </Badge>
            ))}
            {uniquePhases.length > 4 ? (
              <Badge variant="secondary" className="text-[10px]">
                +{uniquePhases.length - 4}
              </Badge>
            ) : null}
          </div>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      case 'terminal':
        return terminalCount > 0 ? (
          <span className="tabular-nums">{terminalCount}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      case 'updatedAt':
        return <span className="text-muted-foreground">{formatDate(pipeline.updatedAt)}</span>;
    }
  }

  const filterContent = (
    <>
      <DataTableFilterSection
        label="Pipeline"
        active={!!search}
        onClear={() => {
          setSearch('');
          setPage(1);
        }}
      >
        <DataTableSearchFilterField
          value={search}
          onValueChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          placeholder="Search by pipeline, stage, or phase..."
        />
      </DataTableFilterSection>
      <DataTableFilterSection
        label="Default"
        active={defaultFilter !== 'all'}
        onClear={() => {
          setDefaultFilter('all');
          setPage(1);
        }}
      >
        <DataTableSelectFilterField
          value={defaultFilter}
          onValueChange={(value) => {
            setDefaultFilter(value as DefaultFilter);
            setPage(1);
          }}
          placeholder="All pipelines"
          options={DEFAULT_FILTER_OPTIONS}
        />
      </DataTableFilterSection>
    </>
  );

  return (
    <>
      <Fullbleed />
      <DetailPageLayout
        header={
          <>
            <div className="min-w-0">
              <h1 className="truncate text-base font-semibold">Job Pipelines</h1>
              <p className="text-xs text-muted-foreground">
                Define the workflows and stages that jobs move through operationally.
              </p>
            </div>
            <Button
              size="icon-sm"
              title="Create Job Pipeline"
              aria-label="Create job pipeline"
              onClick={openCreateDrawer}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </>
        }
        contentClassName="flex min-h-0 flex-col p-0"
      >
        <div className="hidden min-h-0 flex-1 md:flex">
          <FinancialDataTable
            columns={PIPELINE_COLUMNS}
            rows={pagePipelines}
            getRowKey={(pipeline) => pipeline.id}
            renderCell={(key, pipeline) => renderCell(key, pipeline)}
            storageKey="settings-job-pipelines-table"
            emptyIcon={GitBranch}
            emptyTitle={pipelines.length === 0 ? 'No pipelines yet' : 'No matching pipelines found'}
            searchValue={search}
            onSearchChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            searchPlaceholder="Search job pipelines..."
            showClearAll={Boolean(search) || activeFilterCount > 0}
            onClearAll={resetFilters}
            activeFilterCount={activeFilterCount}
            filterSheet={{
              onReset: resetFilters,
              resetDisabled: activeFilterCount === 0 && !search,
              content: filterContent,
            }}
            showExport={false}
            isLoading={isLoading}
            skeletonRowCount={8}
            onRowClick={(pipeline) => openDetail(pipeline.id)}
            renderRowActions={(pipeline) => (
              <PipelineRowActions
                isDefault={Boolean(pipeline.isDefault)}
                onEdit={() => openEditDrawer(pipeline.id)}
                onMakeDefault={() => updatePipeline.mutate({ id: pipeline.id, isDefault: true })}
                onArchive={() => setArchiveTarget(pipeline)}
              />
            )}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={handleSort}
            pagination={{
              count: sortedPipelines.length,
              singular: 'pipeline',
              page: currentPage,
              totalPages,
              onPrevious: () => setPage((current) => Math.max(1, current - 1)),
              onNext: () => setPage((current) => Math.min(totalPages, current + 1)),
            }}
          />
        </div>

        <JobPipelinesMobileList
          pipelines={pagePipelines}
          totalCount={sortedPipelines.length}
          isLoading={isLoading}
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          showClearAll={Boolean(search) || activeFilterCount > 0}
          activeFilterCount={activeFilterCount}
          filterContent={filterContent}
          onResetFilters={resetFilters}
          currentPage={currentPage}
          totalPages={totalPages}
          onPrevious={() => setPage((current) => Math.max(1, current - 1))}
          onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
          onDetail={(pipelineId) => openDetail(pipelineId)}
          onEdit={(pipelineId) => openEditDrawer(pipelineId)}
          onMakeDefault={(pipelineId) => updatePipeline.mutate({ id: pipelineId, isDefault: true })}
          onArchive={(pipeline) => setArchiveTarget(pipeline)}
        />
      </DetailPageLayout>

      <Sheet
        open={createOpen || Boolean(detailPipelineId) || Boolean(decodedEditPipelineId)}
        onOpenChange={(open) => {
          if (!open) closeDrawer();
        }}
      >
        <SheetContent
          side="right"
          className="w-full gap-0 overflow-y-auto p-0 sm:max-w-2xl"
          showCloseButton={false}
        >
          {createOpen || decodedEditPipelineId ? (
            <PipelineFormContent
              mode={createOpen ? 'create' : 'edit'}
              pipeline={editPipeline}
              isPending={isDrawerPending}
              onClose={closeDrawer}
              onBack={decodedEditPipelineId ? () => openDetail(decodedEditPipelineId) : undefined}
              onCreate={(values, stages) => {
                void handleCreate(values, stages).catch((err: unknown) =>
                  toast.error(err instanceof Error ? err.message : 'Failed to save'),
                );
              }}
              onUpdate={(pipelineId, values, stages) => {
                void handleUpdate(pipelineId, values, stages).catch((err: unknown) =>
                  toast.error(err instanceof Error ? err.message : 'Failed to save'),
                );
              }}
            />
          ) : selectedPipeline ? (
            <PipelineDetailContent
              pipeline={selectedPipeline}
              onEdit={() => openEditDrawer(selectedPipeline.id)}
              onClose={closeDrawer}
            />
          ) : (
            <p className="p-5 text-sm text-muted-foreground">Loading...</p>
          )}
        </SheetContent>
      </Sheet>

      <Dialog
        open={Boolean(archiveTarget)}
        onOpenChange={(open) => {
          if (!open) setArchiveTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive pipeline?</DialogTitle>
            <DialogDescription>
              {archiveTarget ? (
                <>
                  Archive <strong>{archiveTarget.name}</strong>? It will no longer be available for
                  new jobs.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={deletePipeline.isPending}
              onClick={() => setArchiveTarget(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deletePipeline.isPending}
              onClick={() => {
                if (!archiveTarget) return;
                deletePipeline.mutate(archiveTarget.id, {
                  onSuccess: () => setArchiveTarget(null),
                });
              }}
            >
              {deletePipeline.isPending ? 'Archiving...' : 'Archive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PipelineDetailContent({
  pipeline,
  onEdit,
  onClose,
}: {
  pipeline: JobPipeline;
  onEdit: () => void;
  onClose: () => void;
}) {
  const stages = sortedStages(pipeline);

  return (
    <>
      <SheetHeader className="border-b px-5 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border bg-muted">
              <GitBranch className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <SheetTitle className="truncate">{pipeline.name}</SheetTitle>
              {pipeline.isDefault ? (
                <SheetDescription>
                  <Badge variant="secondary" className="gap-1 text-[10px]">
                    <Star className="h-3 w-3" />
                    Default
                  </Badge>
                </SheetDescription>
              ) : null}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Button variant="ghost" size="icon" aria-label="Edit pipeline" onClick={onEdit}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" aria-label="Close" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </SheetHeader>
      <div className="space-y-6 p-5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">
            {stages.length} {stages.length === 1 ? 'stage' : 'stages'}
          </Badge>
          {pipeline.isDefault ? (
            <Badge variant="secondary" className="gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Default
            </Badge>
          ) : null}
        </div>

        {stages.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Stages
            </p>
            <div className="space-y-1">
              {stages.map((stage, index) => (
                <div
                  key={stage.id}
                  className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <span
                      className="h-3 w-3 shrink-0 rounded-full"
                      style={{
                        backgroundColor: stage.color ?? CORE_PHASE_COLORS[stage.corePhase],
                      }}
                    />
                    <span className="truncate font-medium">{stage.name}</span>
                    <Badge variant="outline" className="shrink-0 text-[10px]">
                      {formatCorePhase(stage.corePhase)}
                    </Badge>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {stage.isTerminal ? (
                      <Badge variant="secondary" className="text-[10px]">
                        Terminal
                      </Badge>
                    ) : null}
                    <span className="text-xs text-muted-foreground">{index + 1}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No stages configured.</p>
        )}

        {pipeline.updatedAt || pipeline.createdAt ? (
          <div className="space-y-1 border-t pt-4">
            {pipeline.updatedAt ? (
              <p className="text-xs text-muted-foreground">
                Updated {formatDate(pipeline.updatedAt)}
              </p>
            ) : null}
            {pipeline.createdAt ? (
              <p className="text-xs text-muted-foreground">
                Created {formatDate(pipeline.createdAt)}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </>
  );
}

function JobPipelinesMobileList({
  pipelines,
  totalCount,
  isLoading,
  search,
  onSearchChange,
  showClearAll,
  activeFilterCount,
  filterContent,
  onResetFilters,
  currentPage,
  totalPages,
  onPrevious,
  onNext,
  onDetail,
  onEdit,
  onMakeDefault,
  onArchive,
}: {
  pipelines: JobPipeline[];
  totalCount: number;
  isLoading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  showClearAll: boolean;
  activeFilterCount: number;
  filterContent: ReactNode;
  onResetFilters: () => void;
  currentPage: number;
  totalPages: number;
  onPrevious: () => void;
  onNext: () => void;
  onDetail: (pipelineId: string) => void;
  onEdit: (pipelineId: string) => void;
  onMakeDefault: (pipelineId: string) => void;
  onArchive: (pipeline: JobPipeline) => void;
}) {
  const [filtersOpen, setFiltersOpen] = useState(false);

  return (
    <div className="flex min-h-0 flex-1 flex-col md:hidden">
      <div className="flex items-center gap-2 border-b p-3">
        <div className="min-w-0 flex-1">
          <SearchInput
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search pipelines..."
            className="h-9"
          />
        </div>
        <Button
          variant="outline"
          size="icon"
          className="relative h-9 w-9 shrink-0"
          aria-label="Filters"
          onClick={() => setFiltersOpen(true)}
        >
          <SlidersHorizontal className="h-4 w-4" />
          {activeFilterCount > 0 ? (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] text-primary-foreground">
              {activeFilterCount}
            </span>
          ) : null}
        </Button>
      </div>

      {showClearAll ? (
        <div className="border-b px-3 py-2">
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onResetFilters}>
            Clear filters
          </Button>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="space-y-0">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="flex gap-3 border-b px-4 py-4">
                <div className="h-10 w-10 shrink-0 animate-pulse rounded-md bg-muted" />
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="h-4 w-1/2 animate-pulse rounded bg-muted" />
                  <div className="h-3 w-2/3 animate-pulse rounded bg-muted" />
                </div>
              </div>
            ))}
          </div>
        ) : pipelines.length === 0 ? (
          <div className="flex h-full min-h-64 flex-col items-center justify-center gap-2 p-8 text-center">
            <GitBranch className="h-8 w-8 text-muted-foreground/60" />
            <p className="text-sm font-medium">No matching pipelines found</p>
          </div>
        ) : (
          pipelines.map((pipeline) => (
            <MobilePipelineRow
              key={pipeline.id}
              pipeline={pipeline}
              onDetail={() => onDetail(pipeline.id)}
              onEdit={() => onEdit(pipeline.id)}
              onMakeDefault={() => onMakeDefault(pipeline.id)}
              onArchive={() => onArchive(pipeline)}
            />
          ))
        )}
      </div>

      <DataTablePaginationFooter
        count={totalCount}
        singular="pipeline"
        page={currentPage}
        totalPages={totalPages}
        onPrevious={onPrevious}
        onNext={onNext}
      />

      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetContent side="right" className="flex w-80 flex-col gap-0 p-0" showCloseButton={false}>
          <DataTableDrawerHeader
            title="Filters"
            onClose={() => setFiltersOpen(false)}
            onReset={onResetFilters}
            resetDisabled={!showClearAll}
          />
          <div className="flex-1 overflow-y-auto">
            <div className="px-5">{filterContent}</div>
          </div>
          <DataTableDrawerActionFooter
            onReset={onResetFilters}
            onApply={() => setFiltersOpen(false)}
            resetDisabled={!showClearAll}
          />
        </SheetContent>
      </Sheet>
    </div>
  );
}

function MobilePipelineRow({
  pipeline,
  onDetail,
  onEdit,
  onMakeDefault,
  onArchive,
}: {
  pipeline: JobPipeline;
  onDetail: () => void;
  onEdit: () => void;
  onMakeDefault: () => void;
  onArchive: () => void;
}) {
  const stages = sortedStages(pipeline);

  return (
    <div
      role="button"
      tabIndex={0}
      className="grid grid-cols-[auto_minmax(0,1fr)_auto] gap-3 border-b px-4 py-3"
      onClick={onDetail}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onDetail();
      }}
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border bg-muted">
        <GitBranch className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="min-w-0 space-y-1.5">
        <div className="flex min-w-0 items-center gap-2">
          <p className="truncate text-sm font-medium">{pipeline.name}</p>
          {pipeline.isDefault ? (
            <Badge variant="secondary" className="shrink-0 gap-1 text-[10px]">
              <Star className="h-3 w-3" />
              Default
            </Badge>
          ) : null}
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span>{stages.length} stages</span>
          {stages[0] ? <span className="truncate">Starts: {stages[0].name}</span> : null}
        </div>
      </div>
      <div className="flex shrink-0 items-start gap-1" onClick={(event) => event.stopPropagation()}>
        <PipelineRowActions
          isDefault={Boolean(pipeline.isDefault)}
          onEdit={onEdit}
          onMakeDefault={onMakeDefault}
          onArchive={onArchive}
        />
      </div>
    </div>
  );
}

function PipelineRowActions({
  isDefault,
  onEdit,
  onMakeDefault,
  onArchive,
}: {
  isDefault: boolean;
  onEdit: () => void;
  onMakeDefault: () => void;
  onArchive: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onEdit}>
          <Pencil className="h-3.5 w-3.5" />
          Edit
        </DropdownMenuItem>
        {!isDefault ? (
          <DropdownMenuItem onSelect={onMakeDefault}>
            <Star className="h-3.5 w-3.5" />
            Make default
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={onArchive}>
          <Trash2 className="h-3.5 w-3.5" />
          Archive
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function sortedStages(pipeline: JobPipeline) {
  return (pipeline.stages ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder);
}

function uniqueCorePhases(stages: ReturnType<typeof sortedStages>) {
  const phases = new Map<CorePhase, { corePhase: CorePhase; color: string }>();
  stages.forEach((stage) => {
    if (!phases.has(stage.corePhase)) {
      phases.set(stage.corePhase, {
        corePhase: stage.corePhase,
        color: stage.color ?? CORE_PHASE_COLORS[stage.corePhase],
      });
    }
  });
  return [...phases.values()];
}

function getSortValue(key: PipelineColumn, pipeline: JobPipeline) {
  const stages = sortedStages(pipeline);
  switch (key) {
    case 'pipeline':
      return pipeline.name;
    case 'default':
      return pipeline.isDefault ? 1 : 0;
    case 'stages':
      return stages.length;
    case 'phases':
      return uniqueCorePhases(stages).length;
    case 'terminal':
      return stages.filter((stage) => stage.isTerminal).length;
    case 'updatedAt':
      return pipeline.updatedAt ?? '';
  }
}

function compareValues(left: string | number | boolean, right: string | number | boolean) {
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

function formatDate(value: string | null | undefined) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}
